import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import { MOAT_MOMENTS, LAYER_SCENARIOS } from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "moat-runner";

function computeOutcome(scoutResult, layersResult, ventureResult) {
  const { log } = ventureResult;

  const scoutScore = scoutResult.correctCount * 15;
  const layersScore = layersResult.correctCount * 25;

  const goodCount = log.filter((l) => l.choice.tag === "good").length;
  const weakCount = log.filter((l) => l.choice.tag === "weak").length;
  const badCount = log.filter((l) => l.choice.tag === "bad").length;
  const ventureScore = goodCount * 20 - weakCount * 5 - badCount * 15;

  const score = Math.max(50, scoutScore + layersScore + ventureScore + 50);

  let verdict;
  if (goodCount >= 5) {
    verdict = { label: "Moat Secured — Empire Status", tone: "lime" };
  } else if (goodCount >= 3) {
    verdict = { label: "Toehold Established", tone: "gold" };
  } else {
    verdict = { label: "Still In The Trenches", tone: "danger" };
  }

  return { scoutScore, layersScore, ventureScore, goodCount, weakCount, badCount, verdict, score };
}

export default function DebriefStage({ scoutResult, layersResult, ventureResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(scoutResult, layersResult, ventureResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Moat Runner results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toneClasses = {
    lime: "border-lime bg-lime/10 text-lime",
    gold: "border-gold bg-gold/10 text-gold",
    danger: "border-danger bg-danger/10 text-danger",
  };

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-magenta">EMPIRE REPORT</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">Today's Run</p>

      <div className={`mx-auto mb-4 max-w-sm border-2 p-5 ${toneClasses[outcome.verdict.tone]}`}>
        <p className="label-pixel mb-1">Verdict</p>
        <p className="font-display text-base">{outcome.verdict.label}</p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Moats Read Correctly</span>
          <span className="text-cyan">
            {scoutResult.correctCount}/{MOAT_MOMENTS.length}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">AI Layers Matched</span>
          <span className="text-gold">
            {layersResult.correctCount}/{LAYER_SCENARIOS.length}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Moat Protected</span>
          <span className="text-lime">{outcome.goodCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Survivable, Not Sharp</span>
          <span className="text-gold">{outcome.weakCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Gave Up The Moat</span>
          <span className="text-danger">{outcome.badCount}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        Nobody's fighting you for the boring accounts. The kitchen hood still needs cleaning, the
        elevator still needs its stamp, and 44% of businesses still haven't touched AI. The whole
        edge was just showing up for the parts everyone else scrolled past.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN IT AGAIN
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
